// NEW_FILE_JUSTIFIED: Drag & drop file upload zone for claim supporting documents — integrates with Convex file storage

import { useState, useCallback, useRef } from 'react';
import { CloudUpload, X } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { IFileDropZoneProps, IUploadedFileMetadata } from '@/@types/claims';

export function FileDropZone({
  files,
  onFilesChange,
  onNewFilesUploaded,
  disabled,
}: IFileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const createUploadUrl = useMutation(api.storage.createUploadUrl);

  const uploadFiles = useCallback(
    async (fileList: File[]) => {
      if (fileList.length === 0) return;
      setIsUploading(true);
      try {
        const results = await Promise.allSettled(
          fileList.map(async (file): Promise<IUploadedFileMetadata> => {
            const uploadUrl = await createUploadUrl();
            const result = await fetch(uploadUrl, {
              method: 'POST',
              headers: { 'Content-Type': file.type },
              body: file,
            });
            if (!result.ok) throw new Error(`Upload failed for ${file.name}`);
            const { storageId } = (await result.json()) as { storageId: string }; // AS_JUSTIFIED: Convex upload response shape is documented
            return {
              storageId,
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type,
            };
          })
        );

        const newFiles: IUploadedFileMetadata[] = [];
        let failCount = 0;
        for (const r of results) {
          if (r.status === 'fulfilled') {
            newFiles.push(r.value);
          } else {
            failCount++;
          }
        }

        if (newFiles.length > 0) {
          onFilesChange([...files, ...newFiles]);
          onNewFilesUploaded?.(newFiles);
        }
        if (failCount > 0) {
          toast.error(`${failCount} file${failCount > 1 ? 's' : ''} failed to upload`);
        }
      } catch {
        toast.error('Failed to upload files');
      } finally {
        setIsUploading(false);
      }
    },
    [files, onFilesChange, onNewFilesUploaded, createUploadUrl]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;
      void uploadFiles(Array.from(e.dataTransfer.files));
    },
    [disabled, uploadFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files;
      if (!selected) return;
      void uploadFiles(Array.from(selected));
      e.target.value = '';
    },
    [uploadFiles]
  );

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={e => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-background-secondary py-8 cursor-pointer transition-colors',
          isDragOver && 'border-[#84CC16] bg-[#F7FEE7]',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <CloudUpload className="h-8 w-8 text-muted-foreground" />
        <p className="text-[13px] text-muted-foreground">
          {isUploading ? 'Uploading...' : 'Drag & drop files here, or click to browse'}
        </p>
        <p className="text-xs text-muted-foreground">
          Upload itemized bills, diagnosis reports, prescriptions, receipts
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((file, i) => (
            <li
              key={file.storageId}
              className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-2"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-foreground truncate">{file.fileName}</span>
                <span className="text-xs text-muted-foreground">
                  {(file.fileSize / 1024).toFixed(1)} KB
                </span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="shrink-0 p-1 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
